import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { ClipboardCheck, ChevronRight, CheckCircle2, Circle, ChevronDown, ChevronUp, MessageSquare, FileText, Clock, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceChecklists() {
  const [checklists, setChecklists] = useState([]);
  const [region, setRegion] = useState('');
  const [regionLabel, setRegionLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => { fetchChecklists(); }, []);

  const fetchChecklists = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/compliance-checklists`, { withCredentials: true });
      setChecklists(data.checklists);
      setRegion(data.region);
      setRegionLabel(data.region_label);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load checklists');
    }
    setLoading(false);
  };

  const openChecklist = async (id) => {
    setActiveChecklist(id);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/compliance-checklists/${id}`, { withCredentials: true });
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load checklist');
    }
    setDetailLoading(false);
  };

  const toggleItem = async (ref, currentState) => {
    try {
      await axios.put(`${API}/api/compliance-checklists/${activeChecklist}/items/${encodeURIComponent(ref)}`, {
        completed: !currentState,
      }, { withCredentials: true });
      // Update local state
      setDetail(prev => ({
        ...prev,
        items: prev.items.map(i => i.ref === ref ? { ...i, completed: !currentState, completed_at: !currentState ? new Date().toISOString() : null } : i),
        completed: prev.completed + (!currentState ? 1 : -1),
      }));
      // Update parent list
      setChecklists(prev => prev.map(c => c.id === activeChecklist ? { ...c, completed_count: c.completed_count + (!currentState ? 1 : -1) } : c));
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  const saveNotes = async (ref, notes, evidenceRef) => {
    const item = detail.items.find(i => i.ref === ref);
    try {
      await axios.put(`${API}/api/compliance-checklists/${activeChecklist}/items/${encodeURIComponent(ref)}`, {
        completed: item.completed,
        notes,
        evidence_ref: evidenceRef,
      }, { withCredentials: true });
      setDetail(prev => ({
        ...prev,
        items: prev.items.map(i => i.ref === ref ? { ...i, notes, evidence_ref: evidenceRef } : i),
      }));
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    }
  };

  const backToList = () => {
    setActiveChecklist(null);
    setDetail(null);
    setExpandedItem(null);
    fetchChecklists();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20"><div className="spinner w-8 h-8" /></div>
      </DashboardLayout>
    );
  }

  // Detail view
  if (activeChecklist && detail) {
    const pct = detail.total > 0 ? Math.round((detail.completed / detail.total) * 100) : 0;
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-3xl" data-testid="checklist-detail">
          <button onClick={backToList} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-checklists">
            <ArrowLeft className="w-4 h-4" /> Back to Checklists
          </button>

          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">{detail.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{regionLabel} compliance checklist</p>
          </div>

          {/* Progress */}
          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{detail.completed}/{detail.total} items completed</span>
                <span className={`text-sm font-bold ${pct === 100 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#EF4444' }} />
              </div>
              {detail.assessor && (
                <p className="text-xs text-muted-foreground mt-2">Last assessed by: {detail.assessor}</p>
              )}
            </CardContent>
          </Card>

          {/* Items */}
          <div className="space-y-2">
            {detail.items.map((item, idx) => {
              const isExpanded = expandedItem === item.ref;
              return (
                <Card key={item.ref} className={`border transition-colors ${item.completed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'}`} data-testid={`checklist-item-${item.ref}`}>
                  <CardContent className="p-0">
                    <div className="flex items-start gap-3 p-3">
                      <button
                        onClick={() => toggleItem(item.ref, item.completed)}
                        className="mt-0.5 flex-shrink-0"
                        data-testid={`toggle-${item.ref}`}
                      >
                        {item.completed
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          : <Circle className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                        }
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-xs font-mono text-muted-foreground">{item.ref}</span>
                            <p className={`text-sm ${item.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{item.item}</p>
                          </div>
                          <button onClick={() => setExpandedItem(isExpanded ? null : item.ref)} className="flex-shrink-0 p-1 rounded hover:bg-muted/50">
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                          </button>
                        </div>
                        {item.completed_at && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(item.completed_at).toLocaleDateString()} by {item.completed_by}
                          </p>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 pl-11 space-y-3 border-t border-border/50 pt-3">
                        <div className="bg-muted/30 rounded-lg p-2.5">
                          <p className="text-xs text-muted-foreground"><strong className="text-foreground">Guidance:</strong> {item.guidance}</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                            <MessageSquare className="w-3 h-3" /> Notes / Evidence
                          </label>
                          <textarea
                            defaultValue={item.notes}
                            placeholder="Add assessment notes, findings, or evidence references..."
                            className="w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-xs resize-none"
                            rows={2}
                            data-testid={`notes-${item.ref}`}
                            onBlur={(e) => saveNotes(item.ref, e.target.value, item.evidence_ref)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                            <FileText className="w-3 h-3" /> Evidence Reference
                          </label>
                          <input
                            type="text"
                            defaultValue={item.evidence_ref}
                            placeholder="e.g. Policy v2.1, Audit Report Mar 2026"
                            className="w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-xs"
                            data-testid={`evidence-${item.ref}`}
                            onBlur={(e) => saveNotes(item.ref, item.notes, e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // List view
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="compliance-checklists-page">
        <div>
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-[#00A6A0]" />
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">Compliance Checklists</h1>
              <p className="text-sm text-muted-foreground">Interactive regulatory checklists for {regionLabel}</p>
            </div>
          </div>
        </div>

        {checklists.length === 0 ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center">
              <ClipboardCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground">No checklists available for your region ({regionLabel})</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {checklists.map(cl => {
              const pct = cl.total_items > 0 ? Math.round((cl.completed_count / cl.total_items) * 100) : 0;
              return (
                <Card
                  key={cl.id}
                  className="glass-card border-border hover:border-[#00A6A0]/30 transition-colors cursor-pointer"
                  onClick={() => cl.has_items && openChecklist(cl.id)}
                  data-testid={`checklist-card-${cl.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 pr-3">
                        <p className="text-sm font-medium text-foreground">{cl.name}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-xs text-muted-foreground">{cl.completed_count}/{cl.total_items} items</span>
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden max-w-[120px]">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#EF4444' }} />
                          </div>
                          <span className={`text-xs font-medium ${pct === 100 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-muted-foreground'}`}>{pct}%</span>
                        </div>
                        {cl.last_updated && (
                          <p className="text-[10px] text-muted-foreground mt-1">Updated: {new Date(cl.last_updated).toLocaleDateString()}</p>
                        )}
                      </div>
                      {cl.has_items ? (
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <span className="text-[10px] text-muted-foreground">Coming soon</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
